import type { TravellerCount } from '@itineraries/entities/itinerary';
import {
  aggregateTravellers,
  buildPeoplePayload,
  filterPositiveTravellers,
} from '@itineraries/entities/itinerary';
import { hasTravellers } from '@travel-planner/shared/utils';

export type { TravellerCount } from '@itineraries/entities/itinerary';

// Read whatever's on the chapter (`null`, legacy `[{ id, count }]`, or
// the canonical `{ people: [...] }` shape) and return aggregated counts.
// Empty array signals "Itinerary default" — no per-chapter override.
export const readChapterTravellers = (
  raw: unknown,
  typesById: Map<string, string>
): TravellerCount[] => aggregateTravellers(raw, typesById);

// Are this chapter's travellers a real override, or just "default"?
// `chapter.travellers === null` is the canonical default representation;
// an empty `{ people: [] }` payload is also treated as default so the
// editor can round-trip the toggle without forcing the consultant to
// delete every counter row first. Same shared rule the BE cascade guard
// (`chapterHasTravellerOverride`) applies — an empty composition is unset.
export const isCustomChapterTravellers = (raw: unknown): boolean =>
  hasTravellers(raw);

// Build the jsonb payload the BE cascade writes onto items inside the
// chapter window. Returns `null` when the consultant kept the chapter
// on "Itinerary default" so the BE's cascade-skip kicks in and items'
// existing travellers are preserved.
export const buildChapterTravellersPayload = (
  mode: 'default' | 'custom',
  counts: TravellerCount[]
): unknown => {
  if (mode === 'default') return null;
  const positive = filterPositiveTravellers(counts);
  if (positive.length === 0) return null;
  return buildPeoplePayload(positive);
};
